import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { useApp } from '../../context/AppContext';
import ShopifyIcon from '../ui/ShopifyIcon';
import { SITE_URL, SHOPIFY_STORE_URL, SHOPIFY_MYSHOPIFY_URL } from '../../utils/constants';

const SERVICE_KEYS = ['uxui', 'webdev', 'apis', 'ecommerce', 'social', 'project'];
const COMPANY_HREFS = ['#sobre-nosotros', '#portfolio', '#blog', '#carreras', '#contacto'];
const LEGAL_HREFS = ['/privacy', '/terms', '#cookies', '#legal'];

const Footer = () => {
  const { t, get } = useLanguage();
  const { showToast } = useApp();

  const handleSocialClick = (e) => {
    e.preventDefault();
    showToast(t('common.comingSoon'), 'success');
  };

  const linkCompany = get('footer.linkCompany') || [];
  const linkLegal = get('footer.linkLegal') || [];

  const footerLinks = {
    servicios: SERVICE_KEYS.map((key) => ({ name: t(`services.items.${key}.title`), href: '#servicios' })),
    empresa: linkCompany.map((name, i) => ({ name, href: COMPANY_HREFS[i] || '#' })),
    legal: linkLegal.map((name, i) => ({ name, href: LEGAL_HREFS[i] || '#' }))
  };

  const socialLinks = [
    {
      name: 'LinkedIn',
      ariaKey: 'footer.social.linkedin',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
        </svg>
      )
    },
    {
      name: 'Twitter',
      ariaKey: 'footer.social.twitter',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    },
    {
      name: 'Instagram',
      ariaKey: 'footer.social.instagram',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
        </svg>
      )
    },
    {
      name: 'GitHub',
      ariaKey: 'footer.social.github',
      icon: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>
        </svg>
      )
    }
  ];

  const renderLinkColumn = (title, links) => (
    <div className="footer-column">
      <h3 className="footer-title">{title}</h3>
      <ul className="footer-list">
        {links.map((link, index) => (
          <li key={index}>
            <a
              href={link.href}
              className="footer-link"
              {...(link.external
                ? { target: '_blank', rel: 'noopener noreferrer' }
                : {})}
            >
              {link.name}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-top">
          <div className="footer-grid">
            <div className="footer-brand-col">
              <a href="/" className="footer-logo-link" aria-label="CodevNext">
                <img
                  src="/codevnext-isotipo.png"
                  alt=""
                  className="footer-logo-isotipo"
                  width={64}
                  height={64}
                  decoding="async"
                />
              </a>
              <p className="footer-description">{t('footer.description')}</p>

              <div className="footer-presence">
                <span className="footer-presence-label">{t('footer.presence.label')}</span>
                <ul className="footer-presence-list">
                  <li>
                    <a href={SITE_URL} className="footer-presence-link">
                      app.codevnext.shop
                    </a>
                  </li>
                  <li>
                    <a
                      href={SHOPIFY_STORE_URL}
                      className="footer-presence-link footer-presence-link--shop"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      codevnext.shop
                    </a>
                  </li>
                </ul>
              </div>

              <div className="footer-social" aria-label={t('footer.social.label')}>
                {socialLinks.map((social, index) => (
                  <button
                    key={index}
                    type="button"
                    className="social-link"
                    aria-label={t(social.ariaKey)}
                    onClick={handleSocialClick}
                  >
                    <span className="social-icon" aria-hidden="true">{social.icon}</span>
                  </button>
                ))}
              </div>
            </div>

            {renderLinkColumn(t('footer.links.services'), footerLinks.servicios)}
            {renderLinkColumn(t('footer.links.company'), footerLinks.empresa)}

            <div className="footer-column footer-column-shop">
              <h3 className="footer-title">{t('footer.links.shop')}</h3>
              <div className="footer-shop-card">
                <div className="footer-shop-card-icon" aria-hidden="true">
                  <ShopifyIcon size={40} />
                </div>
                <p className="footer-shop-card-text">{t('footer.shop.description')}</p>
                <a
                  href={SHOPIFY_STORE_URL}
                  className="btn btn-shopify btn-shopify--compact"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('footer.shop.visitStore')}
                </a>
                <a
                  href={SHOPIFY_MYSHOPIFY_URL}
                  className="footer-shop-card-meta"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Shopify · codevnext.myshopify.com
                </a>
              </div>
            </div>
          </div>

          <div className="footer-cta">
            <div className="footer-cta-inner">
              <div className="footer-cta-text">
                <h3 className="footer-cta-title">{t('footer.ctaTitle')}</h3>
                <p className="footer-cta-description">{t('footer.ctaDescription')}</p>
              </div>
              <a href="#contacto" className="btn btn-primary btn-lg footer-cta-btn">
                {t('footer.ctaButton')}
              </a>
            </div>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="footer-copyright">{t('footer.copyright')}</p>
            <nav className="footer-bottom-nav" aria-label={t('footer.links.legal')}>
              {footerLinks.legal.map((link, index) => (
                <a key={index} href={link.href} className="footer-bottom-link">
                  {link.name}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
